import { z } from "zod";
/**
 * AND-BC3 — CE QUE LE SERVEUR PEUT DIRE À UNE APP QU'ON NE PEUT PLUS MODIFIER.
 *
 * Depuis qu'un binaire est publié sur un store, `GET /v1/config` est le seul
 * canal par lequel le serveur atteint une version installée des mois plus tôt.
 * Il ne savait jusqu'ici qu'allumer et éteindre des fonctionnalités : aucun
 * moyen de DIRE quelque chose (maintenance, incident, clé captcha révoquée sur
 * une version précise).
 *
 * DEUX CHOIX QUI NE SE RENÉGOCIENT PAS.
 *
 *  1. AUCUN NIVEAU BLOQUANT. Henri a tranché « tant qu'on peut on maintient » :
 *     on ne coupe jamais une vieille version. `level` ne sert donc qu'à
 *     l'apparence du bandeau — il n'existe volontairement pas de valeur qui
 *     empêcherait d'utiliser l'app. Ajouter un jour « blocking » ici serait
 *     irréversible : les binaires déjà publiés lui obéiraient à jamais.
 *
 *  2. LE TEXTE EST TRADUIT PAR LE SERVEUR. On envoie une phrase, pas un code.
 *     Un binaire figé ne connaîtra JAMAIS le code qu'on inventera en novembre :
 *     lui envoyer `MAINTENANCE_2026_11` le laisserait afficher une chaîne brute
 *     ou rien du tout. C'est la raison d'être du catalogue serveur
 *     (`apps/api/src/lib/i18nServer.ts`), déjà utilisé par les e-mails et les
 *     push, et c'est pour ça que la requête porte la langue du lecteur.
 */
/**
 * Niveaux d'avis. La liste est fermée côté SERVEUR (ce qu'on a le droit
 * d'émettre) et ouverte côté CLIENT (`parseConfigNotice` retombe sur `info`
 * pour tout ce qu'il ne connaît pas) : c'est l'asymétrie qui permet d'ajouter
 * un niveau plus tard sans faire disparaître le message chez les anciens.
 */
export const NOTICE_LEVELS = ["info", "warning"];
export const ConfigNoticeSchema = z.object({
    level: z.enum(NOTICE_LEVELS),
    /** Déjà TRADUIT dans la langue du destinataire, prêt à afficher tel quel. */
    message: z.string().min(1),
    /** Page d'explication (statut, billet d'incident). Absent = pas de lien. */
    learnMoreUrl: z.string().url().optional(),
});
/**
 * Lecture TOLÉRANTE d'un avis reçu, pour le client.
 *
 * `ConfigNoticeSchema.parse` serait fail-closed : un `level` inventé après ce
 * build ferait disparaître le message, c'est-à-dire exactement la panne qu'on
 * cherche à corriger — un serveur qui parle à une app qui n'écoute pas. On
 * retient donc ce qu'on comprend (le texte) et on dégrade ce qu'on ne comprend
 * pas (le niveau) au lieu de tout jeter.
 *
 * Rend `null` quand il n'y a rien d'affichable : absence, forme inattendue,
 * message vide. Les champs inconnus sont ignorés sans erreur.
 */
export function parseConfigNotice(raw) {
    if (raw === null || typeof raw !== "object")
        return null;
    const candidate = raw;
    const message = typeof candidate.message === "string" ? candidate.message.trim() : "";
    if (message === "")
        return null;
    const level = NOTICE_LEVELS.includes(candidate.level)
        ? candidate.level
        : "info";
    const notice = { level, message };
    // Un lien qu'on n'ouvrirait pas (schéma exotique, texte libre) vaut moins que
    // pas de lien : on garde le message et on jette le lien, jamais l'inverse.
    const url = typeof candidate.learnMoreUrl === "string" ? candidate.learnMoreUrl : "";
    if (/^https:\/\/[^\s]+$/.test(url))
        notice.learnMoreUrl = url;
    return notice;
}
//# sourceMappingURL=config.js.map